import { Observable, FetchResult, ApolloClient } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import type { DocumentNode, OperationDefinitionNode, GraphQLFormattedError } from 'graphql';
import {
    refresh, refreshDriver,
    loginAdmin, mutationLoginDriver,
    logoutGql, mutationLogoutDriver,
} from '../GraphQL/auth.graphql';
import { AppRoutes } from '../Constants/Routes';

// Tuning knobs for the refresh path.
const REFRESH_TIMEOUT_MS = 10_000;
const REFRESH_COOLDOWN_MS = 5_000;       // min gap between refresh calls
const REFRESH_BUDGET_MAX = 3;            // max refreshes per window
const REFRESH_BUDGET_WINDOW_MS = 60_000;
const CTX_RETRIED = '__authRetried';

/** Operation name from a gql doc; rename in `.graphql.ts` then breaks tsc here. */
const opName = (doc: DocumentNode): string => {
    const def = doc.definitions.find(
        (d): d is OperationDefinitionNode => d.kind === 'OperationDefinition',
    );
    return def?.name?.value ?? '';
};

/** Auth ops bypass the link entirely — would self-recurse otherwise. */
const AUTH_OPS: ReadonlySet<string> = new Set([
    opName(refresh), opName(refreshDriver),
    opName(loginAdmin), opName(mutationLoginDriver),
    opName(logoutGql), opName(mutationLogoutDriver),
]);

/**
 * Real UNAUTHENTICATED only. Accepts GraphQL extensions.code OR a network-level
 * 401 when no GraphQL errors are present (request failed before reaching the
 * resolver). Bare 401 alongside graphQL errors is rejected — a stressed backend
 * can produce that for non-auth reasons.
 */
const isAuthError = (
    graphQLErrors: ReadonlyArray<GraphQLFormattedError> | undefined,
    networkError: unknown,
): boolean => {
    if (graphQLErrors?.some((e) => e?.extensions?.code === 'UNAUTHENTICATED')) {
        return true;
    }
    if (!graphQLErrors?.length && (networkError as { statusCode?: number })?.statusCode === 401) {
        return true;
    }
    return false;
};

/* ─── Apollo client registration (avoids circular import with GraphqlContext) ── */

let apolloClient: ApolloClient<unknown> | null = null;

/** Wire the Apollo client. Must be called once after the client is constructed. */
export const registerApolloClient = (c: ApolloClient<unknown>): void => {
    apolloClient = c;
};

const getApolloClient = (): ApolloClient<unknown> => {
    if (!apolloClient) {
        throw new Error('authErrorLink: Apollo client not registered. Call registerApolloClient() after constructing the client.');
    }
    return apolloClient;
};

/* ─── Session kind ─────────────────────────────────────────────────────────── */

export type SessionKind = 'admin' | 'driver' | null;

// `null` here means "no resolver registered yet" — different from a resolver
// that returns null (which means "user is logged out").
let sessionKindResolver: (() => SessionKind) | null = null;

/** Registered by AuthProvider on mount to expose Redux-backed session kind. */
export const setSessionKindResolver = (fn: () => SessionKind): void => {
    sessionKindResolver = fn;
};

const isDriverSession = (): boolean => {
    if (sessionKindResolver) {
        // Resolver registered — trust it as the source of truth.
        return sessionKindResolver() === 'driver';
    }
    // Pre-mount fallback only — AuthProvider hasn't registered yet.
    return typeof window !== 'undefined' && localStorage.getItem('typename') === 'Driver';
};

/* ─── Refresh state + helpers ──────────────────────────────────────────────── */

// Single-flight + cooldown + rolling budget. true = cookie rotated;
// false = refresh failed OR breaker tripped (caller treats both the same).
let refreshInFlight: Promise<boolean> | null = null;
let lastRefreshAt = 0;
const refreshTimestamps: number[] = [];

const pruneOldTimestamps = (now: number): void => {
    while (refreshTimestamps.length && now - refreshTimestamps[0] > REFRESH_BUDGET_WINDOW_MS) {
        refreshTimestamps.shift();
    }
};

const canAttemptRefresh = (): boolean => {
    const now = Date.now();
    if (now - lastRefreshAt < REFRESH_COOLDOWN_MS) return false;
    pruneOldTimestamps(now);
    return refreshTimestamps.length < REFRESH_BUDGET_MAX;
};

const recordRefreshAttempt = (): void => {
    const now = Date.now();
    lastRefreshAt = now;
    refreshTimestamps.push(now);
};

// Promise timeout helper. Does NOT cancel the underlying fetch — Apollo's
// link-chain abort plumbing would be required for that.
const withTimeout = <T,>(p: Promise<T>, ms: number): Promise<T> =>
    new Promise<T>((resolve, reject) => {
        const t = setTimeout(() => reject(new Error('refresh timeout')), ms);
        p.then(
            (v) => { clearTimeout(t); resolve(v); },
            (e) => { clearTimeout(t); reject(e); },
        );
    });

const doRefresh = (): Promise<boolean> => {
    if (refreshInFlight) return refreshInFlight;
    if (!canAttemptRefresh()) return Promise.resolve(false);

    const query = isDriverSession() ? refreshDriver : refresh;

    // Sync-throw guard: don't burn a budget slot for an attempt that never went out.
    let refreshPromise: Promise<unknown>;
    try {
        refreshPromise = withTimeout(
            getApolloClient().query({ query, fetchPolicy: 'no-cache' }),
            REFRESH_TIMEOUT_MS,
        );
    } catch {
        return Promise.resolve(false);
    }

    recordRefreshAttempt();
    refreshInFlight = refreshPromise
        .then(() => true)
        .catch(() => false)
        .finally(() => { refreshInFlight = null; });
    return refreshInFlight;
};

const redirectToLogin = (): void => {
    if (typeof window === 'undefined') return;
    if (window.location.pathname === AppRoutes.login) return;
    window.location.assign(`${window.location.origin}${AppRoutes.login}`);
};

/**
 * Apollo error link. On UNAUTHENTICATED:
 *   - rotates the cookie via `refreshAdmin` / `refreshDriver`
 *   - replays the failed operation once on success
 * Budget + cooldown bound the refresh rate as a defensive safety net.
 */
export const authErrorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
    if (operation.getContext()[CTX_RETRIED]) return;
    if (!isAuthError(graphQLErrors, networkError)) return;
    if (AUTH_OPS.has(operation.operationName)) return;

    return new Observable<FetchResult>((observer) => {
        let innerSub: { unsubscribe: () => void } | null = null;
        let cancelled = false;

        const surfaceError = (): void => {
            observer.error(graphQLErrors?.[0] ?? networkError ?? new Error('Session expired'));
        };

        doRefresh().then((ok) => {
            if (cancelled) return;

            if (!ok) {
                // Refresh failed OR breaker tripped — either way we can't
                // save this session. Redirect to /login and surface error.
                redirectToLogin();
                surfaceError();
                return;
            }
            // success → cookie is fresh, replay the original operation.
            operation.setContext({ [CTX_RETRIED]: true });
            try {
                innerSub = forward(operation).subscribe({
                    next: observer.next.bind(observer),
                    error: observer.error.bind(observer),
                    complete: observer.complete.bind(observer),
                });
            } catch (e) {
                observer.error(e as Error);
            }
        });

        // Stop the chain if the caller unsubscribes mid-refresh.
        return () => {
            cancelled = true;
            innerSub?.unsubscribe();
        };
    });
});
